'use strict';

namespace Core {

    import ProductType = Core.Booking.ProductType;
    import CrossSellType = Core.Booking.CrossSellType;

    enum BookingStatus {
        Confirmed= 1,
        Pending= 2,
        Canceled= 3
    };

    export interface IBookingProbability {
        Product: ProductType;
        Probability: number;
    }

    export interface IBooking {
        BookingId: number;
        Status: number;
    }

    // this enum used for mapping cross sells to priorities
    // (required because the API fails to define a ProductType for each cross sell)
    export enum productTypeByCrossSellKey {
        Attractions = ProductType.Attraction,
        Hotels = ProductType.Hotel,
        CarHire = ProductType.CarHire,
        Parking = ProductType.Parking,
        Transfer = ProductType.Transfer
    };

    export enum crossSellTypeByCrossSellKey {
        Attractions = CrossSellType.Attractions,
        Hotels = CrossSellType.Hotels,
        CarHire = CrossSellType.CarHire,
        Parking = CrossSellType.Parking,
        Transfer = CrossSellType.Transfers
    };

    export class CrossSellCommonService {

        private baseUrl = '/api2/crosssell/';
        private sourceId = 2010;
        private crossSellsCache = {};

		constructor(private $http: ng.IHttpService,
                    private $location: ng.ILocationService,
                    private $window: any,
                    private searchUnitService: Search.SearchUnitService,
                    private dateUtil,
                    private $q: ng.IQService,
                    private occupancyService: Core.OccupancyService,
                    private $locale,
                    private currentUser) { }

        public productTypeProperties = {
            Attractions: {
                CrossSellType: crossSellTypeByCrossSellKey['Attractions'],
                ProductType: productTypeByCrossSellKey['Attractions'],
                Icon: 'icon-attractions',
                Code: 'ATTRACTIONS'
            },
            Hotels: {
                CrossSellType: crossSellTypeByCrossSellKey['Hotels'],
                ProductType: productTypeByCrossSellKey['Hotels'],
                Icon: 'icon-hotels',
                Code: 'HOTELS'
            },
            CarHire: {
                CrossSellType: crossSellTypeByCrossSellKey['CarHire'],
                ProductType: productTypeByCrossSellKey['CarHire'],
                Icon: 'icon-car-hire',
                Code: 'CARHIRE'
            },
            Parking: {
                CrossSellType: crossSellTypeByCrossSellKey['Parking'],
                ProductType: productTypeByCrossSellKey['Parking'],
                Icon: 'icon-parking',
                Code: 'PARKING'
            },
            Transfer: {
                CrossSellType: crossSellTypeByCrossSellKey['Transfer'],
                ProductType: productTypeByCrossSellKey['Transfer'],
                Icon: 'icon-transfers',
                Code: 'TRANSFERS'
            }
        };

        getBookingCrossSell(bookingId: string): ng.IPromise<any> {
            let crossSellUrl = this.baseUrl + 'bookingcrosssell/';
            if (this.currentUser.get() && this.currentUser.get().AnonSignedIn) {
                crossSellUrl = this.baseUrl + 'bookingcrosssell/linked/';
            }
            return this.$http.get(crossSellUrl
                    + this.sourceId + '/'
                    + bookingId + '/'
                    + TRPCONFIG.Domain.DomainId,
                { ignoreErrors: true }).catch(_.noop);
        }

        private _trackCrossSell(bookingId: string, productType: ProductType): ng.IPromise<any> {
            return this.$http.get(this.baseUrl
                    + 'trackcrosssell/'
                    + this.sourceId + '/'
                    + bookingId + '/'
                    + TRPCONFIG.Domain.DomainId + '/'
                    + productType,
                { ignoreErrors: true });
        }

        getBasketCrossSell(basketId: string): ng.IPromise<IBookingProbability[]> {
            return this.$http.get(this.baseUrl
                    + 'basketcrosssells/'
                    + TRPCONFIG.Domain.DomainId + '/'
                    + TRPCONFIG.ApplicationType + '/'
                    + basketId,
                { ignoreErrors: true }
            ).then((res: any): ng.IPromise<IBookingProbability[]> => {
                return res.data.Predictions;
            });
        }

        getTripCrossSell(tr, getBookingCrossSell) {
            var isCurrent = this.dateUtil.compareDates(tr.EndDate, this.dateUtil.todayPlus(-1, 0, true)) === 1;
            if (TRPCONFIG.Domain.DashboardCrossSellEnabled &&
                isCurrent &&
                tr.Bookings) {

                if (this.crossSellsCache[tr.TripId]) {
                    return this.$q.when(this.crossSellsCache[tr.TripId]);
                }
                var booking: IBooking = _.find(tr.Bookings, (b: IBooking) => b.Status !== BookingStatus.Canceled);
                if (booking && booking.BookingId && getBookingCrossSell) {
                    return getBookingCrossSell(booking.BookingId).then(cs => {
                        this.crossSellsCache[tr.TripId] = cs;
                        return cs;
                    });
                }
                return this.$q.when();
            }
            return this.$q.when();
        }

        startCrossSellJourney(bookingId, crossSellProduct): void {
            this._trackCrossSell(bookingId, crossSellProduct.ProductType);
            switch (crossSellProduct.CrossSellType) {
                case CrossSellType.Transfers:
                    var direction;
                    if (crossSellProduct.ReturnDate && crossSellProduct.ArrivalDate) {
                        direction = 1;
                    }
                    else if (crossSellProduct.ArrivalDate) {
                        direction = 2;
                    }
                    else if (crossSellProduct.ReturnDate) {
                        direction = 3;
                    }
                    this.searchUnitService.setDirection(direction);

                    let transferIndex = this.searchUnitService.transfer;
                    this.searchUnitService.endDate(transferIndex, crossSellProduct.ReturnDate || null);
                    this.searchUnitService.startDate(transferIndex, crossSellProduct.ArrivalDate || null);

                    this.searchUnitService.setDestinationAirport({
                        Title: crossSellProduct.AirportName,
                        Code: crossSellProduct.AirportCode
                    });

                    this.searchUnitService.setResort({
                        Type: 5, // transfer resort
                        Code: crossSellProduct.ResortLocationId,
                        Description: crossSellProduct.DestinationResort
                    });
                    this.occupancyService.setOccupancy([{
                        Adults: crossSellProduct.Adults,
                        ChildAges: crossSellProduct.ChildAges
                    }]);
                    this.$location.path('transfers').search('source', 'MYB cross-sell');

                    break;
                case CrossSellType.CarHire:
                    this.$window.location = TRPCONFIG.Domain.CarHireCrossSellUrl + '/book?' + $.param({
                            pickupDateTime: crossSellProduct.PickUpDate,
                            returnDateTime: crossSellProduct.DropOffDate,
                            pickupIATACode: crossSellProduct.AirportCode,
                            returnIATACode: crossSellProduct.AirportCode,
                            age:  crossSellProduct.DriverAge,
                            residencyId: this.$locale.id.split('-')[1]
                        });

                    break;
                case CrossSellType.Attractions:
                    this.$window.location = TRPCONFIG.DesktopUrlRoot
                        + '/Attractions/ProductView.aspx?PIV='
                        + crossSellProduct.ProductViewId;

                    break;
                case CrossSellType.Hotels:
                    const hotelIndex = this.searchUnitService.hotel;
                    this.searchUnitService.endDate(hotelIndex, crossSellProduct.CheckOutDate || null);
                    this.searchUnitService.startDate(hotelIndex, crossSellProduct.CheckInDate || null);
                    //We should not go to the availability page directly, take the user to the landing page.
                    this.$location.path(crossSellProduct.Url).search('source', 'MYB cross-sell');

                    break;
                case CrossSellType.Parking:
                    let promise = this.searchUnitService.getAirport(crossSellProduct.AirportCode);

                    let parkingIndex = this.searchUnitService.parking;
                    this.searchUnitService.startDate(parkingIndex, crossSellProduct.DepartureDate || null);
                    this.searchUnitService.endDate(parkingIndex, crossSellProduct.ReturnDate || null);
                    this.occupancyService.setPassengerCount(crossSellProduct.PassengerCount);

                    promise.then(airport => {
                        this.searchUnitService.setDepartureAirports(airport);
                        this.$location.path('parking').search('source', 'MYB cross-sell');
                    });
            }
        }
	}

	angular
		.module('core')
		.service('crossSellCommonService', CrossSellCommonService);
}
